import React, { useEffect, useState } from "react"
import { Card } from "ui/Card"
import { Form, Input, Button, Flex, Select } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import PropTypes from "prop-types"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { colors } from "themes/variables"
import { Text } from "ui/Typography"
import { SelectTag } from "ui/SelectTag"
import { InfoTooltip } from "ui/Tooltip/InfoTooltip"
import { useDispatch, useSelector } from "react-redux"
import { modelSelector } from "store/selectors"
import { getTripSubcategories, seedTripSubcategories } from "store/trips"
import styles from "./details.module.css"

const { TextArea } = Input

/**
 * Wizard-local copy of ExperienceInfo.
 *
 * Key difference from the original: description is read directly from the AntD
 * form store via Form.useWatch instead of a local useState mirror. This prevents
 * the description from being wiped when the component unmounts and remounts
 * (the original's useState("") + useEffect would call form.setFieldsValue({ description: "" })
 * on every remount, erasing the preserved form value).
 */
export const ExperienceInfo = ({ form }) => {
  const [isShowAiModal, setIsShowAiModal] = useState(false)
  const [profileDescription, setProfileDescription] = useState("")
  const [t] = useTranslation()
  const dispatch = useDispatch()

  const descriptionValue = Form.useWatch("description", form) || ""
  const selectedCategory = Form.useWatch("categories", form)
  const { data: tripCategories, loading: categoriesLoading } = useSelector((state) => state.trips.tripCategories)
  const { data: tripSubcategories, loading: subcategoriesLoading } = useSelector((state) => state.trips.tripSubcategories)
  const trip = useSelector(modelSelector("trip"))

  // On edit load: fetch subcategories for the trip's existing category so the Select has options
  useEffect(() => {
    if (trip?.category_id) {
      dispatch(getTripSubcategories(trip.category_id))
    }
  }, [trip?.id, trip?.category_id, dispatch])

  const categoryOptions = tripCategories.map((category) => ({ value: category.id, label: category.name }))
  const subcategoryOptions = tripSubcategories.map((tag) => ({ value: tag.id, label: tag.name }))

  const handleCategoryChange = (value) => {
    form.setFieldsValue({ categories: value, sub_categories: undefined })
    if (value) dispatch(getTripSubcategories(value))
    else dispatch(seedTripSubcategories([]))
  }

  return (
    <>
      <Card title={t("experiences.details.experience_info")} className="mb-xl">
        <Form.Item
          name="title"
          label={t("experiences.details.labels.listing_title")}
          rules={[{ required: true, message: t("experiences.details.listing_title_error") }]}
        >
          <Input placeholder={t("experiences.details.listing_title_placeholder")} />
        </Form.Item>
        <Form.Item
          name="categories"
          label={t("experiences.details.labels.category")}
          rules={[{ required: true, message: t("experiences.details.errors.category_required", "Please select a category") }]}
        >
          <Select
            placeholder={t("experiences.details.select_category_placeholder")}
            className={styles.select}
            options={categoryOptions}
            onChange={handleCategoryChange}
            loading={categoriesLoading}
          />
        </Form.Item>
        <Form.Item
          name="sub_categories"
          label={
            <Flex gap={8} align="center">
              <span>{t("experiences.details.labels.marketplace_tags")}</span>
              <InfoTooltip title={t("experiences.details.tooltips.marketplace_tags")} />
            </Flex>
          }
          rules={[{ required: true, message: t("experiences.details.errors.marketplace_tags_required") }]}
        >
          <Select
            mode="multiple"
            showSearch
            placeholder={
              selectedCategory
                ? t("experiences.details.placeholders.select_tags")
                : t("experiences.details.placeholders.select_category_first", "Select a category first")
            }
            options={subcategoryOptions}
            disabled={!selectedCategory}
            loading={subcategoriesLoading}
            optionFilterProp="label"
            tagRender={SelectTag}
          />
        </Form.Item>
        {/* <div className="mb-m mt-xs sm:text-center">
          <span className="text-sm">
            {t("experiences.details.no_tag_info")}{" "}
            <Button type="link" onClick={() => setIsShowRequestTagModal(true)} style={{ padding: 0 }}>
              {t("experiences.details.request_tag")}
            </Button>
          </span>
        </div> */}
        <Flex justify="space-between" align="center" className="w-full mb-xs">
          <Text className={styles.formItemLabel}>{t("global.description")} *</Text>
          <Button
            type="primary"
            icon={
              <Icon
                name="aiEnhancement"
                color={descriptionValue.length < 25 ? colors.textPrimary : colors.disabled}
                height={20}
                width={20}
              />
            }
            onClick={() => setIsShowAiModal(true)}
            disabled={descriptionValue.length < 25}
          >
            {t("client.enhance_with_ui")}
          </Button>
        </Flex>
        <Form.Item name="description" noStyle rules={[{ required: true, message: t("experiences.details.description_required_error") }]}>
          <TextArea placeholder={t("experiences.details.description_placeholder")} rows={4} />
        </Form.Item>
      </Card>
      {isShowAiModal && (
        <AiEnhancementModal
          open={isShowAiModal}
          description={descriptionValue}
          setDescription={(val) => form.setFieldsValue({ description: val })}
          profileDescription={profileDescription}
          setProfileDescription={setProfileDescription}
          onOk={() => setIsShowAiModal(false)}
          onCancel={() => {
            setIsShowAiModal(false)
            setProfileDescription("")
          }}
          text={t("client.experience_content")}
          modalTitle={t("client.experiences_enhancer")}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("client.refinement_tooltip")}
          improveWithAITooltipTitle={t("client.improve_with_ai_tooltip_for_experiences")}
        />
      )}
    </>
  )
}

ExperienceInfo.propTypes = {
  form: PropTypes.object.isRequired
}
